/**
 * Application state: goal, personal targets, comparison selection.
 *
 * Persisted to localStorage so preferences survive a reload and remain
 * available offline. Nothing here is sent to a server, and no account is
 * required.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { GoalId } from '@/lib/nutrition/scoring';

/** Optional personal targets (spec section 21). All nullable; none required. */
export interface Targets {
  dailyKcal: number | null;
  dailyProtein: number | null;
  maxSpend: number | null;
  minProtein: number | null;
  maxKcal: number | null;
}

export const EMPTY_TARGETS: Targets = {
  dailyKcal: null,
  dailyProtein: null,
  maxSpend: null,
  minProtein: null,
  maxKcal: null,
};

export const MAX_COMPARE = 6;

interface AppStateValue {
  goal: GoalId;
  setGoal: (goal: GoalId) => void;
  sortId: string;
  setSortId: (id: string) => void;
  targets: Targets;
  setTargets: (targets: Targets) => void;
  /**
   * Leaderboard criteria the user has chosen, in selection order.
   *
   * Held here rather than in LeaderboardPage because the product page needs it:
   * opening a product must surface its rank for the criteria you were actually
   * looking at, and local page state does not survive the navigation.
   */
  criteria: string[];
  /**
   * Which selected criterion leads.
   *
   * With several criteria chosen the combined score decides the order, but a
   * reader still needs ONE number per row to read the list by. The lead
   * criterion supplies it, and breaks ties in the combined score so the top of
   * the list is ordered by what the reader said mattered most.
   *
   * Null means "the first one selected".
   */
  leadCriterion: string | null;
  setLeadCriterion: (id: string | null) => void;
  setCriteria: (next: string[] | ((current: string[]) => string[])) => void;
  /** Product ids selected for comparison, in selection order. */
  compareIds: string[];
  /**
   * `name` is remembered so the comparison tray stays readable after the
   * originating result list unmounts.
   */
  toggleCompare: (id: string, name?: string) => void;
  /** Display name for a tracked id, when we have seen one. */
  compareName: (id: string) => string | null;
  removeCompare: (id: string) => void;
  clearCompare: () => void;
  isComparing: (id: string) => boolean;
  compareFull: boolean;
}

const AppStateContext = createContext<AppStateValue | null>(null);

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private-browsing quota errors must not break the app.
  }
}

export function AppStateProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [goal, setGoalRaw] = useState<GoalId>(() => readStored('kcaltrack.goal', 'max_protein'));
  const [sortId, setSortIdRaw] = useState<string>(() =>
    readStored('kcaltrack.sort', 'protein_per_dollar'),
  );
  const [targets, setTargetsRaw] = useState<Targets>(() =>
    readStored('kcaltrack.targets', EMPTY_TARGETS),
  );
  const [compareIds, setCompareIds] = useState<string[]>(() =>
    readStored('kcaltrack.compare', [] as string[]),
  );
  const [names, setNames] = useState<Record<string, string>>(() =>
    readStored('kcaltrack.names', {} as Record<string, string>),
  );
  const [criteria, setCriteriaRaw] = useState<string[]>(() =>
    readStored('kcaltrack.criteria', ['cheapest_per_100g']),
  );
  const [leadCriterion, setLeadCriterionRaw] = useState<string | null>(() =>
    readStored('kcaltrack.lead', null as string | null),
  );

  useEffect(() => writeStored('kcaltrack.goal', goal), [goal]);
  useEffect(() => writeStored('kcaltrack.sort', sortId), [sortId]);
  useEffect(() => writeStored('kcaltrack.targets', targets), [targets]);
  useEffect(() => writeStored('kcaltrack.compare', compareIds), [compareIds]);
  useEffect(() => writeStored('kcaltrack.names', names), [names]);
  useEffect(() => writeStored('kcaltrack.criteria', criteria), [criteria]);
  useEffect(() => writeStored('kcaltrack.lead', leadCriterion), [leadCriterion]);

  const toggleCompare = useCallback((id: string, name?: string) => {
    if (name !== undefined) {
      setNames((current) => (current[id] === name ? current : { ...current, [id]: name }));
    }
    setCompareIds((current) => {
      if (current.includes(id)) return current.filter((x) => x !== id);
      if (current.length >= MAX_COMPARE) return current;
      return [...current, id];
    });
  }, []);

  const removeCompare = useCallback((id: string) => {
    setCompareIds((current) => current.filter((x) => x !== id));
  }, []);

  const value = useMemo<AppStateValue>(
    () => ({
      goal,
      setGoal: setGoalRaw,
      sortId,
      setSortId: setSortIdRaw,
      targets,
      setTargets: setTargetsRaw,
      criteria,
      setCriteria: setCriteriaRaw,
      leadCriterion,
      setLeadCriterion: setLeadCriterionRaw,
      compareIds,
      toggleCompare,
      compareName: (id: string) => names[id] ?? null,
      removeCompare,
      clearCompare: () => setCompareIds([]),
      isComparing: (id: string) => compareIds.includes(id),
      compareFull: compareIds.length >= MAX_COMPARE,
    }),
    [goal, sortId, targets, criteria, leadCriterion, compareIds, names, toggleCompare, removeCompare],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext);
  if (ctx === null) throw new Error('useAppState must be used inside AppStateProvider');
  return ctx;
}

/** Tracks browser connectivity, for the offline / stale-data strip. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
